// How the ads did › Suggested changes, Blueprint only (pages/ux-full screens/ads.js, "Suggested ad
// changes"): the queue from adChanges.list, each change opened in a sheet to approve or dismiss with why
// not (adChanges.decide), then marked done once someone has made it in the platform (adChanges.markDone).
// Flowology writes nothing to Google Ads, Microsoft Ads or Local Services: an approved change is one to
// make there by hand. The weekly review runs from the worker; "Run the review now" runs it on demand.
import { useState } from 'react';
import {
  AdChange,
  AdChangeList,
  AdChangeProps,
  AdChangesProps,
  Section,
  type AdChangeDecision,
  type AdChangeRow,
  type Host,
} from '@flowology/ui-web';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { Sheet } from '../shared/EditSheet.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { Loading } from '../shared/Loading.tsx';
import { CLOSED_CHANGES, mayDecideAdChanges, OPEN_CHANGES, reviewWords } from './ads-model.ts';

type List = { changes: AdChangeRow[]; next: string | null; waiting: number };

export function AdChangesSection({
  timeZone,
  held,
  host,
}: {
  timeZone: string;
  held: readonly string[];
  host: Host;
}) {
  const [closed, setClosed] = useState(false);
  const q = useQuery<List>('adChanges.list', { status: closed ? CLOSED_CHANGES : OPEN_CHANGES, limit: 50 });
  const [openId, setOpenId] = useState<string | null>(null);
  // the answer to a decision, shown until the list has it too
  const [answered, setAnswered] = useState<AdChangeRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const decides = mayDecideAdChanges(held);
  // one key per attempt, so pressing again after a lost answer decides it once
  const approveKey = useSaveKey(`approve:${openId ?? ''}`);
  const dismissKey = useSaveKey(`dismiss:${openId ?? ''}`);
  const doneKey = useSaveKey(`done:${openId ?? ''}`);
  const reviewKey = useSaveKey('review');
  const shown = answered?.id === openId ? answered : (q.data?.changes.find((c) => c.id === openId) ?? null);

  const call = async (action: string, input: object, key: { key: string; renew: () => void }) => {
    setBusy(true);
    setError(null);
    try {
      const out = await client.call<AdChangeRow>(action, input, { idempotencyKey: key.key });
      key.renew();
      setAnswered(out);
      await q.reload();
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const decide = (d: AdChangeDecision) =>
    void call(
      'adChanges.decide',
      { id: openId, decision: d.decision, ...(d.note ? { note: d.note } : {}) },
      d.decision === 'approve' ? approveKey : dismissKey,
    );
  const review = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await client.call<{ found: number; added: number; refreshed: number; quiet: number }>(
        'adChanges.review',
        {},
        { idempotencyKey: reviewKey.key },
      );
      reviewKey.renew();
      setSaid(reviewWords(r));
      if (closed) setClosed(false);
      else await q.reload();
    } catch (e) {
      setSaid(errorText(e, 'The review didn’t run. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    setOpenId(null);
    setAnswered(null);
    setError(null);
  };

  return (
    <Section title="Suggested changes" {...(q.data ? { count: q.data.waiting } : {})}>
      {(() => {
        if (q.data == null)
          return <Loading error={q.error} what="the suggested changes" retry={() => void q.reload()} />;
        const parsed = AdChangesProps.safeParse({
          timeZone,
          changes: q.data.changes,
          waiting: q.data.waiting,
        });
        if (!parsed.success)
          return (
            <p className="flw-empty" role="status">
              Flowology can’t show the suggested changes here.
            </p>
          );
        return (
          <AdChangeList
            data={parsed.data}
            host={host}
            onOpen={setOpenId}
            {...(closed ? { empty: 'Nothing dismissed or done yet.' } : {})}
          />
        );
      })()}
      <p className="flw-acts">
        {decides && (
          <button type="button" className="flw-link" disabled={busy} onClick={() => void review()}>
            {busy && !openId ? 'Running the review…' : 'Run the review now'}
          </button>
        )}
        <button type="button" className="flw-link" onClick={() => setClosed((c) => !c)}>
          {closed ? 'Show what waits' : 'Show the dismissed and done'}
        </button>
      </p>
      {said && (
        <p className="flw-sub" role="status">
          {said}
        </p>
      )}
      {shown && (
        <Sheet title={shown.status === 'waiting' ? 'A suggested ad change' : 'An ad change'} onClose={close}>
          {(() => {
            const parsed = AdChangeProps.safeParse({ timeZone, change: shown });
            if (!parsed.success)
              return (
                <p className="flw-empty" role="status">
                  Flowology can’t show this change here.
                </p>
              );
            return (
              <AdChange
                data={parsed.data}
                host={host}
                busy={busy}
                cannotDecide={decides ? null : 'Decided by the owner or the manager.'}
                onDecide={decide}
                {...(decides
                  ? { onDone: () => void call('adChanges.markDone', { id: openId }, doneKey) }
                  : {})}
              />
            );
          })()}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="row">
            <button className="btn quiet" type="button" onClick={close} disabled={busy}>
              Close
            </button>
          </div>
        </Sheet>
      )}
    </Section>
  );
}
